import { Fragment, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, normalizeSellerId, SELLER_ID_PATTERN } from '@seller/shared';
import { api, type SellerAliasList } from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import { invalidateSellerNames, SellerLink, SellerNameForm } from '../components/Sellers.js';
import { Alert, Button, Card, Input, PageHeader, Spinner, TableScroll } from '../components/ui.js';
import { day, integer } from '../format.js';

/**
 * Known sellers (`/offers/sellers`): the competitors the owner has named.
 *
 * Amazon's Pricing API names each competing offer's seller only by an opaque id;
 * the SP-API has no seller-name lookup and the app never scrapes Amazon's pages.
 * So the owner opens a seller's public profile (every id links to it) and names
 * it here or from an Offers card. The names are the owner's own labels; they show
 * on every Offers card and in the offers digest mail. Reached from the Offers
 * card's "Known sellers" link, not the sidebar. See docs/offers.md.
 */
export function KnownSellersPage() {
  const session = useSession();
  const canEdit = session.status === 'signedIn' && can(session.user.role, 'data:import');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['sellers'],
    queryFn: () => api.get<SellerAliasList>('/api/sellers'),
  });

  const remove = useMutation({
    mutationFn: (sellerId: string) => api.delete(`/api/sellers/${encodeURIComponent(sellerId)}`),
    onSuccess: () => invalidateSellerNames(queryClient),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Known sellers"
        description="Your own names for competitor sellers. Amazon identifies a competing offer's seller only by an id, and the app never looks names up on Amazon's pages, so open a seller's public profile (every id links to it) and name it here or from an Offers card. The names show on every Offers card and in the offers digest email."
      />

      {canEdit && <NameBySellerId />}

      {query.isPending && <Spinner label="Loading sellers" />}
      {query.isError && <Alert>{query.error.message}</Alert>}
      {remove.isError && <Alert>{remove.error.message}</Alert>}

      {query.data && (
        <Card className="p-5">
          {query.data.sellers.length === 0 ? (
            <p className="text-sm text-muted">
              No sellers named yet. Use &ldquo;Name this seller&rdquo; on an Offers card
              {canEdit ? ', or name one by its id above' : ''}.
            </p>
          ) : (
            <TableScroll>
              <table className="w-full text-sm" aria-label="Known sellers">
                <thead className="border-b border-line text-left text-xs text-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium">Seller</th>
                    <th className="px-3 py-2 font-medium">Note</th>
                    <th className="px-3 py-2 text-right font-medium">Seen on</th>
                    <th className="px-3 py-2 font-medium">Last seen</th>
                    <th className="px-3 py-2 font-medium">Named by</th>
                    {canEdit && (
                      <th className="px-3 py-2 font-medium">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {query.data.sellers.map((s) => (
                    <Fragment key={s.sellerId}>
                      <tr>
                        <td className="px-3 py-1.5">
                          <SellerLink
                            sellerId={s.sellerId}
                            name={s.name}
                            marketplaceId={query.data.marketplaceId}
                          />
                        </td>
                        <td className="px-3 py-1.5 text-muted">{s.note ?? '—'}</td>
                        <td className="tabular px-3 py-1.5 text-right">
                          {integer(s.listings)} listing{s.listings === 1 ? '' : 's'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5">
                          {s.lastSeenAt ? day(s.lastSeenAt) : 'Never'}
                        </td>
                        <td className="px-3 py-1.5 text-muted">{s.createdByName ?? '—'}</td>
                        {canEdit && (
                          <td className="whitespace-nowrap px-3 py-1.5 text-right">
                            <button
                              type="button"
                              className="text-xs text-brand-600 hover:underline"
                              onClick={() => setEditing(s.sellerId)}
                              aria-label={`Edit seller ${s.sellerId}`}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="ml-3 text-xs text-danger-text hover:underline disabled:opacity-50"
                              disabled={remove.isPending}
                              aria-label={`Delete the name for seller ${s.sellerId}`}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Forget the name "${s.name}" for ${s.sellerId}? The seller will show as its id again.`,
                                  )
                                ) {
                                  remove.mutate(s.sellerId);
                                }
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        )}
                      </tr>
                      {editing === s.sellerId && (
                        <tr>
                          <td colSpan={6} className="px-3 pb-3">
                            <SellerNameForm sellerId={s.sellerId} onDone={() => setEditing(null)} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Card>
      )}
    </div>
  );
}

/**
 * Names a seller by its id before (or without) opening an Offers card: the id is
 * uppercased and checked here the way the API does, then the usual name form
 * takes over — and it opens on the stored name and note when the id already has
 * one, so this edits that name instead of wiping its note.
 */
function NameBySellerId() {
  const [draft, setDraft] = useState('');
  const [sellerId, setSellerId] = useState<string | null>(null);
  const trimmed = normalizeSellerId(draft);
  const valid = SELLER_ID_PATTERN.test(trimmed);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (valid) setSellerId(trimmed);
  }

  return (
    <Card className="p-5">
      <h2 className="text-sm font-medium text-body">Name a seller by id</h2>
      {sellerId === null ? (
        <form onSubmit={onSubmit} className="mt-2 flex flex-wrap items-end gap-2">
          <label className="block min-w-48 flex-1">
            <span className="mb-1 block text-xs text-muted">Amazon seller id</span>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value.toUpperCase())}
              placeholder="The id from the seller's profile link"
              aria-invalid={trimmed !== '' && !valid}
            />
          </label>
          <Button type="submit" variant="secondary" disabled={!valid}>
            Continue
          </Button>
          {trimmed !== '' && !valid && (
            <p className="w-full text-xs text-danger-text">
              A seller id is letters and digits only.
            </p>
          )}
        </form>
      ) : (
        <>
          <p className="mt-1 font-mono text-xs text-muted">{sellerId}</p>
          <SellerNameForm
            sellerId={sellerId}
            onDone={() => {
              setSellerId(null);
              setDraft('');
            }}
          />
        </>
      )}
    </Card>
  );
}
